import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Handshake } from "lucide-react";
import { toast } from "sonner";
import { submitPartnership } from "@/lib/api";

const initial = {
    organization: "",
    contact_name: "",
    email: "",
    phone: "",
    partnership_type: "government",
    message: "",
};

const TYPES = [
    { v: "government", l: "Government Official" },
    { v: "sponsor", l: "Sponsor" },
    { v: "community", l: "Community Org" },
    { v: "industry", l: "Industry Pro" },
];

const TIERS = [
    {
        v: "Mini Mogul",
        age: "Elementary Age",
        logo: "/assets/mini-mogul-logo.png",
    },
    {
        v: "Junior Mogul",
        age: "Junior High Age",
        logo: "/assets/junior-mogul-logo.png",
    },
    {
        v: "Young Mogul",
        age: "High School Age",
        logo: "/assets/young-mogul-logo.png",
    },
];

export const PartnerModal = ({ open, mode = "partner", onClose }) => {
    const isSponsor = mode === "sponsor";
    const isMeeting = mode === "meeting";
    const [data, setData] = useState(initial);
    const [tier, setTier] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    const toggleTier = (v) =>
        setTier((curr) =>
            curr.includes(v) ? curr.filter((t) => t !== v) : [...curr, v],
        );

    const onChange = (e) =>
        setData({ ...data, [e.target.name]: e.target.value });

    // Compute the mode once, then drive every dependent value off a lookup
    // object — avoids the nested `isSponsor ? ... : isMeeting ? ... : ...`
    // ternary chain that was sprinkled across the JSX below.
    let modeOf;
    if (isSponsor) modeOf = "sponsor";
    else if (isMeeting) modeOf = "meeting";
    else modeOf = "partner";

    const partnershipType = isSponsor || isMeeting ? modeOf : data.partnership_type;

    const HEADER_LABEL = {
        sponsor: "Sponsorship Inquiry",
        meeting: "Schedule Meeting",
        partner: "Partnership Inquiry",
    };

    const MESSAGE_PLACEHOLDER = {
        sponsor: "Tell us about your sponsorship interest...",
        meeting: "Tell us what you'd like to meet about...",
        partner: "Tell us about your partnership ideas...",
    };

    const HEADLINE = {
        sponsor: { prefix: "Sponsor a ", accent: "9 Week Accelerator Program" },
        meeting: { prefix: "Schedule A ", accent: "Meeting With Our Team" },
        partner: { prefix: "Join The ", accent: "Young Mogul Ecosystem" },
    };

    const successMessage = {
        sponsor: "Sponsorship inquiry received. We'll follow up shortly.",
        meeting: "Meeting request received. We'll follow up shortly.",
        partner: "Partnership inquiry received. We'll follow up shortly.",
    }[modeOf];

    const submit = async (e) => {
        e.preventDefault();
        if (isSponsor && tier.length === 0) {
            toast.error("Please select at least one program tier to sponsor.");
            return;
        }
        setSubmitting(true);
        try {
            await submitPartnership({
                ...data,
                partnership_type: partnershipType,
                message: isSponsor
                    ? `[Sponsoring: ${tier.join(", ")}] ${data.message}`
                    : data.message,
            });
            toast.success(successMessage);
            setData(initial);
            setTier([]);
            onClose();
        } catch (err) {
            toast.error("Submission failed. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-md flex items-start sm:items-center justify-center p-4 py-8 sm:py-12 overflow-y-auto"
                    onClick={onClose}
                    data-testid="partner-modal-overlay"
                >
                    <motion.div
                        initial={{ scale: 0.95, y: 20 }}
                        animate={{ scale: 1, y: 0 }}
                        exit={{ scale: 0.95, y: 20 }}
                        onClick={(e) => e.stopPropagation()}
                        className="relative w-full max-w-2xl my-12 bg-[#0A0A0A] border border-[#D4AF37]/40 gold-glow"
                        data-testid="partner-modal"
                    >
                        <div className="p-8 md:p-12">
                            <button
                                onClick={onClose}
                                data-testid="partner-modal-close"
                                className="absolute top-5 right-5 h-10 w-10 border border-white/15 grid place-items-center text-white/60 hover:text-[#D4AF37] hover:border-[#D4AF37]"
                            >
                                <X size={18} />
                            </button>

                            <div className="flex items-center gap-3 mb-2">
                                <Handshake size={16} className="text-[#D4AF37]" />
                                <span className="text-[11px] uppercase tracking-[0.4em] text-[#D4AF37] font-bold">
                                    {HEADER_LABEL[modeOf]}
                                </span>
                            </div>
                            <h3 className="font-display text-3xl md:text-4xl uppercase tracking-tight leading-[1] text-white">
                                {HEADLINE[modeOf].prefix}
                                <span className="gold-text-gradient">
                                    {HEADLINE[modeOf].accent}
                                </span>
                            </h3>

                            <form
                                onSubmit={submit}
                                className="mt-8 space-y-4"
                                data-testid="partner-form"
                            >
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <Field
                                        label="Organization"
                                        name="organization"
                                        value={data.organization}
                                        onChange={onChange}
                                        required
                                        testId="partner-org"
                                    />
                                    <Field
                                        label="Contact Name"
                                        name="contact_name"
                                        value={data.contact_name}
                                        onChange={onChange}
                                        required
                                        testId="partner-contact"
                                    />
                                </div>
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <Field
                                        label="Email"
                                        name="email"
                                        type="email"
                                        value={data.email}
                                        onChange={onChange}
                                        required
                                        testId="partner-email"
                                    />
                                    <Field
                                        label="Phone"
                                        name="phone"
                                        value={data.phone}
                                        onChange={onChange}
                                        required
                                        testId="partner-phone"
                                    />
                                </div>

                                {!isSponsor && !isMeeting && (
                                    <div>
                                        <label className="text-[11px] uppercase tracking-[0.3em] text-white/50">
                                            Type *
                                        </label>
                                        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
                                            {TYPES.map((t) => (
                                                <button
                                                    type="button"
                                                    key={t.v}
                                                    data-testid={`partner-type-${t.v}`}
                                                    onClick={() =>
                                                        setData({
                                                            ...data,
                                                            partnership_type: t.v,
                                                        })
                                                    }
                                                    className={`text-xs uppercase tracking-[0.2em] py-3 border transition ${
                                                        data.partnership_type ===
                                                        t.v
                                                            ? "bg-[#D4AF37] text-black border-[#D4AF37]"
                                                            : "border-white/15 text-white/65 hover:border-[#D4AF37]/50"
                                                    }`}
                                                >
                                                    {t.l}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {isSponsor && (
                                    <div>
                                        <label className="text-[11px] uppercase tracking-[0.3em] text-white/50">
                                            Select Program Tier(s) *
                                            <span className="ml-2 text-white/30 normal-case tracking-normal text-[10px]">
                                                Choose one or more
                                            </span>
                                        </label>
                                        <div className="mt-3 grid grid-cols-3 gap-3">
                                            {TIERS.map((t) => {
                                                const active = tier.includes(t.v);
                                                return (
                                                    <button
                                                        type="button"
                                                        key={t.v}
                                                        onClick={() => toggleTier(t.v)}
                                                        aria-pressed={active}
                                                        data-testid={`sponsor-tier-${t.v.toLowerCase().replace(/\s/g, "-")}`}
                                                        className={`relative group flex flex-col items-center text-center p-3 border transition-all ${
                                                            active
                                                                ? "border-[#D4AF37] bg-[#D4AF37]/[0.08] gold-glow"
                                                                : "border-white/10 hover:border-[#D4AF37]/50 hover:bg-[#D4AF37]/[0.03]"
                                                        }`}
                                                    >
                                                        {active && (
                                                            <span className="absolute top-1.5 right-1.5 h-4 w-4 rounded-full bg-[#D4AF37] text-black grid place-items-center text-[10px] font-bold">
                                                                ✓
                                                            </span>
                                                        )}
                                                        <div className="h-16 w-16 flex items-center justify-center">
                                                            <img
                                                                src={t.logo}
                                                                alt={`${t.v} Program`}
                                                                className={`max-h-full max-w-full object-contain transition-all ${
                                                                    active
                                                                        ? "drop-shadow-[0_0_18px_rgba(212,175,55,0.6)] scale-105"
                                                                        : "drop-shadow-[0_0_10px_rgba(212,175,55,0.25)] group-hover:scale-105"
                                                                }`}
                                                            />
                                                        </div>
                                                        <div className="mt-2 font-display text-xs uppercase tracking-tight text-white leading-tight">
                                                            {t.v}
                                                        </div>
                                                        <div className="text-[8px] uppercase tracking-[0.25em] text-[#D4AF37] mt-1 font-bold">
                                                            {t.age}
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                <div>
                                    <label className="text-[11px] uppercase tracking-[0.3em] text-white/50">
                                        {isMeeting ? "Meeting Topic *" : "Message *"}
                                    </label>
                                    <textarea
                                        name="message"
                                        value={data.message}
                                        onChange={onChange}
                                        rows={5}
                                        required
                                        data-testid="partner-message"
                                        placeholder={MESSAGE_PLACEHOLDER[modeOf]}
                                        className="mt-2 w-full bg-black/40 border border-white/10 px-4 py-3 text-white placeholder:text-white/30 focus:border-[#D4AF37] outline-none resize-none"
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={submitting}
                                    data-testid="partner-submit"
                                    className="w-full inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-black font-display uppercase tracking-widest py-4 hover:bg-[#FFF3A1] transition-all disabled:opacity-50"
                                >
                                    {submitting
                                        ? "Submitting..."
                                        : "Send Inquiry"}
                                    <Send size={16} />
                                </button>
                            </form>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

const Field = ({ label, testId, ...rest }) => (
    <div>
        <label className="text-[11px] uppercase tracking-[0.3em] text-white/50">
            {label}
            {rest.required && (
                <span className="text-[#D4AF37] ml-1">*</span>
            )}
        </label>
        <input
            {...rest}
            data-testid={testId}
            className="mt-2 w-full bg-black/40 border border-white/10 px-4 py-3 text-white placeholder:text-white/30 focus:border-[#D4AF37] outline-none"
        />
    </div>
);
